import React, { createContext, type ReactNode, useContext, useMemo } from 'react'
import { ChangeGroupImageOverlay } from '@fs/zion-group-management'
import { useOverlay, type UseOverlayReturnOptions } from '@fs/zion-ui'
import { type GroupSummary } from '@fs/zion-tree-types'

type GroupManagementValue = {
  group: GroupSummary
  refetchGroup: () => void
  onLeaveGroup: () => void
  changeImageOverlay: UseOverlayReturnOptions
}

const GroupManagementGroupContext = createContext<GroupManagementValue | undefined>(undefined)

export const useGroupManagementGroupContext = (): GroupManagementValue | undefined =>
  useContext(GroupManagementGroupContext)

type GroupManagementGroupProviderProps = Omit<GroupManagementValue, 'changeImageOverlay'> & {
  children: ReactNode
}

export default function GroupManagementGroupProvider({
  children,
  group,
  refetchGroup,
  onLeaveGroup,
}: GroupManagementGroupProviderProps): JSX.Element {
  const changeImageOverlay = useOverlay()
  const value = useMemo(
    () => ({
      group,
      refetchGroup,
      onLeaveGroup,
      changeImageOverlay,
    }),
    [group, refetchGroup, onLeaveGroup, changeImageOverlay]
  )
  return (
    <GroupManagementGroupContext.Provider value={value}>
      {children}
      <ChangeGroupImageOverlay group={group} overlay={changeImageOverlay} onGroupImageChange={refetchGroup} />
    </GroupManagementGroupContext.Provider>
  )
}
